import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../../constants/common'
import { useUserData } from '../../../../hooks'
import { isStaffUser } from '../../../../utils/user'
import { NotificationInline } from '../../../Notification'
import { getEmptyStateActions, PageEmptyState } from '../../../PageEmptyState'
import { useRequestProfileContext } from '../../RequestProfile.hooks'
import {
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_ADD_NEW_PHASE,
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASES_LIMIT,
  REQUEST_PROFILE_TAB_ACTIVITIES_PHASES_MAX_COUNT,
} from '../RequestProfileTabActivities.constants'
import {
  useAddActivityModal,
  useAddPhaseModal,
  useRequestProfileTabActivitiesContext,
} from '../RequestProfileTabActivities.hooks'
import {
  getCanAddRequestProfileTabActivitiesPhase,
  getRequestProfileTabActivitiesPhasesForUser,
  isRequestProfileTabActivitiesPhasesLimitReached,
} from '../RequestProfileTabActivities.utils'
import { RequestProfileTabActivitiesAddActivityButton } from '../RequestProfileTabActivitiesAddActivityButton'
import { RequestProfileTabActivitiesAddPhaseButton } from '../RequestProfileTabActivitiesAddPhaseButton'
import { RequestProfileTabActivitiesPhaseList } from '../RequestProfileTabActivitiesPhaseList'
import { RequestProfileTabActivitiesSkeleton } from '../RequestProfileTabActivitiesSkeleton'

import * as styles from './RequestProfileTabActivitiesContent.module.css'

function RequestProfileTabActivitiesContent() {
  const { t } = useTranslation()

  const currentUser = useUserData()

  const { outboundUuid, requestData } = useRequestProfileContext()

  const { hasError, isLoading, phases } =
    useRequestProfileTabActivitiesContext()

  const handleAddNewActivity = useAddActivityModal()

  const { handleAddPhase } = useAddPhaseModal()

  const permissions = requestData?.permissions

  const isStaff = isStaffUser(currentUser)

  // Staff see every activity; connected (iDWELL-to-iDWELL) requests already
  // come pre-filtered by the id2id endpoint, so they are shown in full too.
  const canViewAll = isStaff || Boolean(outboundUuid)

  const canEditActivities = isStaff && !outboundUuid

  const canAddPhase = getCanAddRequestProfileTabActivitiesPhase({
    outboundUuid,
    permissions,
  })

  const isPhasesLimitReached =
    isRequestProfileTabActivitiesPhasesLimitReached(phases)

  const displayPhases = getRequestProfileTabActivitiesPhasesForUser({
    canViewAll,
    phases: phases ?? EMPTY_LIST,
  })

  function handleAddPhaseToEnd() {
    handleAddPhase()
  }

  const emptyStateActions = getEmptyStateActions({
    actionName: QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_ADD_NEW_PHASE,
    disabled: isPhasesLimitReached,
    isLight: true,
    qaName: QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_ADD_NEW_PHASE,
    shouldShowActions: canAddPhase && !hasError,
    text: t('RequestProfileTabActivities.AddNewPhaseBtnKey'),
    tooltip: isPhasesLimitReached
      ? t('RequestProfileTabActivities.PhasesLimitReachedTooltipKey')
      : undefined,
    onClick: handleAddPhaseToEnd,
  })

  return isLoading ? (
    <RequestProfileTabActivitiesSkeleton />
  ) : hasError || displayPhases.length === 0 ? (
    <PageEmptyState
      actions={emptyStateActions}
      className={styles.emptyState}
      text={t(
        hasError
          ? 'Error.CommonApiRequestFailKey'
          : 'RequestProfileTabActivities.NoPhasesKey'
      )}
    />
  ) : (
    <div className={styles.RequestProfileTabActivitiesContent}>
      {canAddPhase && isPhasesLimitReached ? (
        <NotificationInline
          qaName={QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASES_LIMIT}
          text={t('RequestProfileTabActivities.PhasesLimitReachedKey', {
            count: REQUEST_PROFILE_TAB_ACTIVITIES_PHASES_MAX_COUNT,
          })}
          type={NOTIFICATION_TYPE.warning}
        />
      ) : null}
      {permissions?.can_add_activity ? (
        <RequestProfileTabActivitiesAddActivityButton
          className={styles.addActivity}
          onClick={handleAddNewActivity}
        />
      ) : null}
      <RequestProfileTabActivitiesPhaseList
        canAddPhase={canAddPhase}
        canEditActivities={canEditActivities}
        isPhasesLimitReached={isPhasesLimitReached}
        permissions={permissions}
        phases={displayPhases}
        onAddPhase={handleAddPhase}
      />
      {canAddPhase ? (
        <RequestProfileTabActivitiesAddPhaseButton
          className={styles.addPhase}
          isLimitReached={isPhasesLimitReached}
          onClick={handleAddPhaseToEnd}
        />
      ) : null}
    </div>
  )
}

export default RequestProfileTabActivitiesContent
